import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { Nullable } from '@jamesgmarks/utilities';
import { IInvoiceGroups, IInvoiceGroupAddressOverrides } from '@llws/lift-entity-interfaces';
import GeoCityDropdown from 'src/components/parts/GeoCityDropdown';
import { isValidPostalCode } from 'src/app-utils/helpers';
import { useAppSelector } from 'src/redux/hooks';
import { getOne } from 'src/redux/features/dynamic-hydra/actions';
import { set } from 'date-fns';
import { formatDecimalToPercentage } from '@hydra/internal';
import { GeoCity } from '../../../../../entities/hydra';

const defaultAddressOverride: IInvoiceGroupAddressOverrides = {
  invoiceGroupId: 0,
  useClientAddress: 1,
  address: '',
  organization: '',
  careOf: '',
  city: '',
  cityId: null,
  province: '',
  postal: '',
  country: '',

};

interface AddressOverrideModalProps {
  open: boolean;
  selectedGroupId: Nullable<number>;
  clientName: Nullable<string>
  initialOverride: Nullable<IInvoiceGroupAddressOverrides>;
  onSave: (value: IInvoiceGroupAddressOverrides, groupId: number) => void;
  onClose: () => void;
}

const AddressOverrideModal: React.FC<AddressOverrideModalProps> = ({
  open,
  selectedGroupId,
  clientName,
  initialOverride,
  onSave,
  onClose,
}) => {
  const [draftAddressOverride, setDraftAddressOverride] = useState<IInvoiceGroupAddressOverrides>(
    initialOverride ?? defaultAddressOverride,
  );

  useEffect(() => {
    if (open) {
      setDraftAddressOverride(initialOverride ?? defaultAddressOverride);
    }
  }, [open, initialOverride]);

  const handleSave = () => {
    if ((!draftAddressOverride.useClientAddress && !draftAddressOverride.cityId) || !selectedGroupId) {
      return;
    }
    onSave(draftAddressOverride, selectedGroupId);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Address Override - {selectedGroupId}</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={1}>
          <Box mt={1} mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!draftAddressOverride.useClientAddress}
                  onChange={(_, checked) =>
                    setDraftAddressOverride((prev) => ({
                      ...prev,
                      useClientAddress: checked ? 1 : 0,
                    }))
                  }
                  color="primary"
                />
              }
              label="Use Client Address"
            />
          </Box>
          <TextField
            label="Organization"
            placeholder={draftAddressOverride.organization ?? clientName ?? ''}
            value={draftAddressOverride.organization ?? ''}
            onChange={e =>
              setDraftAddressOverride((prev) => ({
                ...prev,
                organization: e.target.value,
              }))
            }
            fullWidth
          />
          <TextField
            label="Care Of"
            value={draftAddressOverride.careOf ?? ''}
            onChange={e =>
              setDraftAddressOverride((prev) => ({
                ...prev,
                careOf: e.target.value,
              }))
            }
            fullWidth
          />
          <TextField
            label="Street Address"
            value={draftAddressOverride.address ?? ''}
            onChange={e =>
              setDraftAddressOverride((prev) => ({
                ...prev,
                address: e.target.value,
              }))
            }
            fullWidth
            required={!!draftAddressOverride.useClientAddress}
            disabled={!!draftAddressOverride.useClientAddress}
            error={!draftAddressOverride.useClientAddress && !draftAddressOverride.address?.trim()}
            helperText={
              !draftAddressOverride.useClientAddress && !draftAddressOverride.address?.trim()
                ? 'Street address is required when not using client address'
                : ''
            }
          />
          <GeoCityDropdown
            cityId={draftAddressOverride.cityId ?? null}
            onCityChange={(city) => {
              setDraftAddressOverride((prev) => ({
                ...prev,
                cityId: city?.id ?? null,
                city: city?.cityName ?? '',
                province: city?.geoProvince.provinceName ?? '',
                country: city?.geoProvince.geoCountry.countryName ?? '',
              }));

            }}
            disabled={!!draftAddressOverride.useClientAddress}
          />
          <TextField
            label="Postal Code"
            value={draftAddressOverride.postal ?? ''}
            onChange={e =>
              setDraftAddressOverride((prev) => ({
                ...prev,
                postal: e.target.value,
              }))
            }
            required={!!draftAddressOverride.useClientAddress}
            disabled={!!draftAddressOverride.useClientAddress}
            error={!draftAddressOverride.useClientAddress && !isValidPostalCode(draftAddressOverride.postal ?? '')}
            helperText={
              !draftAddressOverride.useClientAddress && !isValidPostalCode(draftAddressOverride.postal ?? '')
                ? "Invalid USA or Canadian postal code"
                : ""
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!draftAddressOverride.useClientAddress && !draftAddressOverride.cityId}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressOverrideModal;